import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { usePost, get_deployment } from '../API';

export type Transaction = {
  action_id: string;
  transaction_id: string | null;
  target_field: string | null;
  amount: number;
  subledger: string | null;
  payment_provider: string;
  rc_status: string;
  transaction_state: string | null;
  transaction_clear_date: string | null;
  proposed_date: string | null;
  uid: string | null;
  entry_type: string;
  card_id: string | null;
  failure_message: string | null;
  trace_id: string | null;
  givecard_recipient_id: string | null;
  attempt: number;
};

type TransactionsContextType = {
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  fetchTransactions: (startDate?: string, endDate?: string) => Promise<void>;
  paymentProviderOptions: string[];
};

const TransactionsContext = createContext<TransactionsContextType | undefined>(undefined);

export const useTransactionsContext = () => {
  const context = useContext(TransactionsContext);
  if (!context) {
    throw new Error('useTransactionsContext must be used within a TransactionsProvider');
  }
  return context;
};

type TransactionsProviderProps = {
  children: ReactNode;
};

export const TransactionsProvider: React.FC<TransactionsProviderProps> = ({ children }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentProviderOptions, setPaymentProviderOptions] = useState<string[]>([]);

  const postTransactionDetails = usePost('/api/admin_panel/transaction_details');
  const postPaymentProviderOptions = usePost('/api/admin_panel/payment_provider_options');

  const fetchTransactions = useCallback(async (startDate?: string, endDate?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await postTransactionDetails({
        program: get_deployment(),
        startDate,
        endDate,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setTransactions(response.data?.transactions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [postTransactionDetails]);

  // This gets all payment provider options used in this program - we should always provide all of these 
  // as an option in the filters. 
  useEffect(() => {
    (async () => {
      try {
        const response = await postPaymentProviderOptions({});
        if (response.error) {
          setError(response.error.message);
        }
        setPaymentProviderOptions(response.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setPaymentProviderOptions([]);
      }
    })();
  }, []);

  const value: TransactionsContextType = {
    transactions,
    isLoading,
    error,
    fetchTransactions,
    paymentProviderOptions
  };

  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  );
};
